import { PropsWithChildren, useEffect, useState } from "react";
import AuthService from "utils/authService";
import { useIsAuthenticated, useLogin } from "core/iam/AuthenticationHooks";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { onboardingLogin } from "generated/api/onboarding-authentication-controller/onboarding-authentication-controller";
import { clearCsrfToken } from "lib/SpringAxios";
import { AxiosError } from "axios";
import { AccessDeniedError } from "foundations/Error/AccessDeniedError";
import { GenericErrorMessage } from "foundations/Error/GenericErrorMessage";
import { useIntl } from "react-intl";
import { whoAmI } from "generated/api/iam/iam";

export function AuthTokenDetector(props: PropsWithChildren) {
  const params = new URLSearchParams(window.location.search);
  const ssoToken = params.get("userToken");
  const onboardingToken = params.get("token");
  const relayState = params.get("RelayState");
  const location = useRouterState({ select: (s) => s.location });

  const intl = useIntl();
  const [onboardingTokenError, setOnboardingTokenError] = useState<
    "tokenInvalid" | "unknown"
  >();

  const isLoggedIn = useIsAuthenticated();
  const stateLogin = useLogin();
  const navigate = useNavigate();

  const handleJwtLogin = async (jwt: string) => {
    // Set the provided JWT in local storage
    AuthService.storeJwt(jwt);
    // Clear the cached CSRF token — the session changes after login, which causes the server to
    // rotate the CSRF token, making any previously cached token stale.
    clearCsrfToken();
    // Validate the JWT
    const user = await whoAmI().catch(() => undefined);
    // If the JWT is invalid, discard it and return
    if (!user) {
      AuthService.discardJwt();
      return;
    }
    // Otherwise, if the JWT is valid, update the contexts
    stateLogin(user);

    // Restore the path the user was on before SSO took over. RelayState is only a path when it
    // isn't a JSON payload — the MS Teams tab flow round-trips its own metadata through it, which
    // we don't consume.
    if (relayState && !isJsonPayload(relayState)) {
      void navigate({ to: relayState });
    }
  };

  // Detect the "userToken" query param received after an SSO login
  // FIXME: The token is a valid JWT but should be a single use token exchanged for authentication
  // The reason is the JWT might be logged by various middlewares on the client or server side and presents
  // a risk of replay attacks if the token can be reused.
  useEffect(() => {
    if (ssoToken && ssoToken !== AuthService.getJwt()) {
      void handleJwtLogin(ssoToken);
    }
  }, [ssoToken]);

  // Detect the onboarding (AKA enrollment) invitation token, which is just the "token" query param
  useEffect(() => {
    if (
      onboardingToken &&
      !isLoggedIn &&
      // Ignore the 'token' param if we're on the password reset page because its param is also
      // called 'token'
      !/password_reset\/?$/.exec(location.pathname)
    ) {
      onboardingLogin({ jwt: onboardingToken })
        .then((resp) => {
          return handleJwtLogin(resp.jwt ?? "");
        })
        // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
        .catch((error: AxiosError) => {
          if (error.response?.status === 401) {
            setOnboardingTokenError("tokenInvalid");
          } else {
            setOnboardingTokenError("unknown");
          }
          console.error("Failed to login with invitation token:", error);
        });
    }
  }, [onboardingToken]);

  if (onboardingTokenError === "tokenInvalid") {
    return (
      <AccessDeniedError
        message={intl.formatMessage({
          defaultMessage:
            "Sorry, this invitation link is no longer valid. " +
            "If you've already enrolled, please visit the homepage to log in.",

          description:
            "Access Denied message when an email invitation link is no longer valid.",
        })}
      />
    );
  }
  if (onboardingTokenError === "unknown") {
    return (
      <GenericErrorMessage
        type="error"
        title={intl.formatMessage({
          defaultMessage: "An error has occurred",
          description:
            "Error message when an unknown error occurs during onboarding login",
        })}
      />
    );
  }

  return props.children;
}

function isJsonPayload(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}
